import React from "react";
import { NumericFormat } from "react-number-format";
import { parseISO } from "date-fns";
import { toZonedTime, format } from "date-fns-tz";
import { useCheckout } from "context/CheckoutContext";
import { useClient } from "ClientContext";
import getSymbolFromCurrency from "currency-symbol-map";

interface QuoteDetailsProps {
  clientName: string;
  Quote?: any; // Use your specific type here if available
  IMG: string;
  TITLE: string;
  ADDRESS: any;
}

const QuoteDetails: React.FC<QuoteDetailsProps> = ({
  IMG,
  TITLE,
  ADDRESS,
}) => {
  // Guard: if Quote is not defined, do not render this component.
  const { clientName, internal_ID } = useClient();
  const { context_quote } = useCheckout();
  const Quote = context_quote;
  if (!Quote) return null;

  const { checkInDateLocalized, checkOutDateLocalized, } = Quote;

  function convertIsoToYDM(isoString: string): string {
    const parsedDate = parseISO(isoString);
    const utcDate = toZonedTime(parsedDate, "UTC");
    return format(utcDate, "yyyy-MM-dd", { timeZone: "UTC" });
  }

  const checkIn = convertIsoToYDM(checkInDateLocalized || "");
  const checkOut = convertIsoToYDM(checkOutDateLocalized || "");

  function decodeHtml(html: string) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    return doc.documentElement.textContent || "";
  }

  const currencySign = getSymbolFromCurrency("USD");

  return (
    <div className="w-full flex flex-col rounded-2xl border border-neutral-200 space-y-6 sm:space-y-8 p-6 ">
      <div className="flex flex-col sm:flex-row sm:items-center">
        <div className="flex-shrink-0 w-full sm:w-40">
          <div className="aspect-w-4 aspect-h-3 sm:aspect-h-4 rounded-2xl overflow-hidden">
            <img
              alt="Property"
              className="absolute inset-0 object-cover"
              sizes="200px"
              src={IMG}
            />
          </div>
        </div>
        <div className="py-5 sm:px-5 space-y-3">
          <div>
            <span className="text-sm text-neutral-500 dark:text-neutral-400 line-clamp-1">
              {clientName}
            </span>
          </div>
          <span className="block text-lg font-semibold text-neutral-800 dark:text-neutral-200">
            {TITLE}
          </span>
          <span className="block text-sm text-neutral-500 dark:text-neutral-400">
            {`${ADDRESS?.city}, ${ADDRESS?.state}, ${ADDRESS?.country}`}
          </span>
          <div className="w-10 border-b border-neutral-200 dark:border-neutral-700"></div>
        </div>
      </div>

      <div className="flex justify-between text-sm text-neutral-600 dark:text-neutral-400">
        <div>
          <span className="font-semibold">Check-in: </span>
          {checkIn}
        </div>
        <div>
          <span className="font-semibold">Check-out: </span>
          {checkOut}
        </div>
        <div>
          <span className="font-semibold">Guests: </span>
          {Quote.guestsCount}
        </div>
      </div>

      <div className="flex flex-col space-y-4">
        <h3 className="text-2xl font-semibold">Price detail</h3>
        <div className="flex justify-between text-neutral-600 dark:text-neutral-300">
          <span>
            <NumericFormat
              value={Quote.nightlyPrice}
              displayType="text"
              thousandSeparator
              prefix="$"
              decimalScale={2}
              fixedDecimalScale={true}
            />{" "}
            x {Quote.lengthOfStay} days
          </span>
          <span>
            <NumericFormat
              value={Quote.preTotal}
              displayType="text"
              thousandSeparator
              prefix="$"
              decimalScale={2}
              fixedDecimalScale={true}
            />
          </span>
        </div>

        {/* Promotion Section */}
        {Quote?.promoItems && Quote.promoItems.length > 0 ? (
          <>
            <div className="flex justify-between text-neutral-600 dark:text-neutral-300">
              <span>Promotion</span>
              <span>
                <NumericFormat
                  value={Quote.totalPromo}
                  displayType="text"
                  thousandSeparator
                  prefix={Quote.promoItems[0]?.type === "PERCENT" ? "" : currencySign}
                  suffix={Quote.promoItems[0]?.type === "PERCENT" ? "%" : ""}
                  decimalScale={0}
                  fixedDecimalScale={true}
                />
              </span>
            </div>
            {Quote.promoItems.map((promo, index) => (
              <div
                key={index}
                className="ml-4 flex justify-between text-neutral-500 dark:text-neutral-400"
              >
                <span>
                  {decodeHtml(
                    (promo as any).name || (promo as any)?.title || ""
                  )}
                </span>
              </div>
            ))}
          </>
        )
      :
      ("")
      }

        {/* Addons Section */}
        {Quote?.addonsItems && Quote.addonsItems.length > 0 && (
          <>
            <div className="flex justify-between text-neutral-600 dark:text-neutral-300">
              <span>Add-Ons</span>
              <span>
                <NumericFormat
                  value={Quote.totalAddons}
                  displayType="text"
                  thousandSeparator
                  prefix="$"
                  decimalScale={2}
                  fixedDecimalScale={true}
                />
              </span>
            </div>
            {Quote.addonsItems.map((addon, index) => (
              <div
                key={index}
                className="ml-4 flex justify-between text-neutral-500 dark:text-neutral-400"
              >
                <span>
                  {decodeHtml(
                    (addon as any).name || (addon as any)?.title || ""
                  )}
                </span>
                <NumericFormat
                  value={addon.amount}
                  displayType="text"
                  thousandSeparator
                  prefix="$"
                  decimalScale={2}
                  fixedDecimalScale={true}
                />
              </div>
            ))}
          </>
        )}

        {/* Fees */}
        <div className="flex justify-between text-neutral-600 dark:text-neutral-300">
          <span>Fees</span>
          <span>
            <NumericFormat
              value={Quote.totalFees}
              displayType="text"
              thousandSeparator
              prefix="$"
              decimalScale={2}
              fixedDecimalScale={true}
            />
          </span>
        </div>
        {Quote.feesItems?.map((fee, index) => (
          <div
            key={index}
            className="ml-4 flex justify-between text-neutral-500 dark:text-neutral-400"
          >
            <span>{fee.title}</span>
            <NumericFormat
              value={fee.amount}
              displayType="text"
              thousandSeparator
              prefix="$"
              decimalScale={2}
              fixedDecimalScale={true}
            />
          </div>
        ))}

        {/* Taxes */}
        <div className="flex justify-between text-neutral-600 dark:text-neutral-300">
          <span>Taxes</span>
          <span>
            <NumericFormat
              value={Quote.totalTaxes}
              displayType="text"
              thousandSeparator
              prefix="$"
              decimalScale={2}
              fixedDecimalScale={true}
            />
          </span>
        </div>
        {Quote.taxesItems?.map((tax, index) => (
          <div
            key={index}
            className="ml-4 flex justify-between text-neutral-500 dark:text-neutral-400"
          >
            <span>{tax.title}</span>
            <NumericFormat
              value={tax.amount}
              displayType="text"
              thousandSeparator
              prefix="$"
              decimalScale={2}
              fixedDecimalScale={true}
            />
          </div>
        ))}

        <div className="border-b border-neutral-200 dark:border-neutral-700"></div>
        <div className="flex justify-between font-semibold">
          <span>Total</span>
          <span>
            <NumericFormat
              value={Quote.stayTotal}
              displayType="text"
              thousandSeparator
              prefix={`${Quote.currency} $`}
              decimalScale={2}
              fixedDecimalScale={true}
            />
          </span>
        </div>

        {/* Payment Policy */}
        {internal_ID === "837ed442-fe16-4e60-8fc5-ff7b175411e3" &&

          (
            <>
              <div className="text-sm text-neutral-500 dark:text-neutral-400 mt-4">
                <strong>Payment Policy:</strong> 50% of the total amount will be charged
                immediately, and the remaining 50% will be charged 14 days before check-in.
              </div>

              <div className="text-sm text-neutral-500 dark:text-neutral-400">
                <strong>Cancellation Policy:</strong>
                <ul className="list-disc pl-5 mt-1">
                  <li>100% refund up to 30 days before arrival</li>
                  <li>50% refund up to 14 days before arrival</li>
                  <li>No refund for cancellations made less than 14 days before arrival</li>
                </ul>
              </div>
            </>
          )

        }

      </div>
    </div>
  );
};

export default QuoteDetails;